import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BACKEND_URL } from "@/lib/api";

export default function AiWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    { role: "assistant", content: "Bonjour ! Je suis TravelBuddy AI. Je peux vous aider à publier un trajet, chercher un buddy, ou expliquer le séquestre. Que puis-je faire ?" },
  ]);
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [messages, streaming]);

  if (!user) return null;

  const send = async () => {
    const q = text.trim();
    if (!q || streaming) return;
    setText("");
    setMessages(m => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("tb_token")}` },
        body: JSON.stringify({ message: q, session_id: `ui_${user.user_id}` }),
      });
      if (!r.ok) throw new Error("http " + r.status);
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value);
        setMessages(m => {
          const cp = [...m];
          cp[cp.length - 1] = { role: "assistant", content: cp[cp.length - 1].content + chunk };
          return cp;
        });
      }
    } catch (e) {
      console.error("ai chat", e);
      setMessages(m => {
        const cp = [...m];
        cp[cp.length - 1] = { role: "assistant", content: "Erreur, réessayez." };
        return cp;
      });
    } finally { setStreaming(false); }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="ai-widget-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full btn-coral flex items-center justify-center shadow-2xl"
          title="TravelBuddy AI"
        >
          <Sparkles size={22} />
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-40 w-[min(380px,calc(100vw-2.5rem))] glass-strong rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: "70vh" }}>
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Sparkles size={14} className="text-[#F59E0B]" /> TravelBuddy AI
            </div>
            <button data-testid="ai-widget-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
          </header>
          <div ref={boxRef} className="flex-1 overflow-y-auto p-3 space-y-2 text-sm" style={{ minHeight: 220 }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-gradient-to-br from-[#FF6B6B] to-[#FA5252] text-white" : "bg-slate-800/70"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={12} className="animate-spin" /> : null)}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-slate-800 p-2 flex gap-2">
            <input
              data-testid="ai-widget-input"
              disabled={streaming}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Posez votre question…"
              className="flex-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none focus:border-[#FF6B6B] disabled:opacity-60"
            />
            <button data-testid="ai-widget-send" disabled={streaming || !text.trim()} className="btn-coral px-3 py-2 rounded-lg text-sm disabled:opacity-50">
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
